import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AccountRecoveryQuestionAnswer, AccountRecoveryQuestionAnswerResponse, AccountRecoveryQuestionResponse } from '../models/account-recovery/accoun-recovery-question';
import { Bandeira } from '../models/bandeira.enum';
import BrowserUtils from '../utils/browser-utils';
import { VerificationMethodTypeEnum } from 'src/app/password-recovery/token-confirmation/token-confirmation.component';
import { ApiService } from '../api/api.service';

export type AuthPasswordRecovery = {
  bandeira: Bandeira;
  cpf: string;
  recaptcha: string;
}

export type UserRecoveryMethodData = {
  email: string;
  phone: string;
  token: string;
}

export type SelectedVerificationMethodInfo = {
  selectedMethod: 'PHONE' | 'EMAIL' | VerificationMethodTypeEnum;
  selectedMethodData: string;
  methodsData: {
    phone: string;
    email: string;
  }
};

export type ValidateTokenResponse = {
  secret: string;
};

export type SecretData = {
  secret: string;
  token: string;
};

type ChangePasswordData = {
  token: string;
  secret: string;
  senha: string;
};

@Injectable({
  providedIn: 'root'
})
export class PasswordRecoveryService {
  private readonly USER_PASSWORD_RECOVERY_TOKEN = '@portal-cb/rcv-token'
  private readonly VERIFICATION_METHOD_INFO = '@portal-cb/vmi'
  private readonly SECRET_KEY = '@portal-cb/sct-key'

  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) { }

  async authPasswordRecovery(authParams: AuthPasswordRecovery): Promise<UserRecoveryMethodData> {
    const { recaptcha, ...params } = authParams;
    const headers = new HttpHeaders().set('g-recaptcha', recaptcha);

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/cadastro/senha/auth',
      oldBrowserFallbackPath: '/cobrancas/cadastro/senha/auth'
    })

    return this.http.post<UserRecoveryMethodData>(path, params, { headers }).toPromise();
  }

  async sendPasswordRecoveryToken(methodType: 'PHONE' | 'EMAIL' | VerificationMethodTypeEnum, isInternal = false): Promise<void> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: `/cobranca-orquestrador-registro/cobrancas/cadastro/senha/token/${methodType}`,
      oldBrowserFallbackPath: `/cobrancas/cadastro/senha/token/${methodType}`
    })

    if (isInternal) {
      return this.http.get<void>(path).toPromise();
    }

    const headers = new HttpHeaders().set('x-authentication-token', this.getPasswordRecoveryAuthToken());
    return this.http.get<void>(path, { headers }).toPromise();
  }

  async validatePasswordRecoveryToken(token: string, isInternal = false): Promise<ValidateTokenResponse> {
    const data = { token: token.toLowerCase() };

    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/cadastro/senha/token/validar',
      oldBrowserFallbackPath: '/cobrancas/cadastro/senha/token/validar'
    })

    if (isInternal) {
      return this.http.post<ValidateTokenResponse>(path, data).toPromise();
    }

    const headers = new HttpHeaders().set('x-authentication-token', this.getPasswordRecoveryAuthToken());
    return this.http.post<ValidateTokenResponse>(path, data, { headers }).toPromise();
  }

  async saveNewPassword(data: ChangePasswordData, isInternal = false): Promise<ValidateTokenResponse> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/cadastro/senha/alterar',
      oldBrowserFallbackPath: '/cobrancas/cadastro/senha/alterar'
    })

    if (isInternal) {
      return this.http.post<ValidateTokenResponse>(path, data).toPromise();
    }

    const headers = new HttpHeaders().set('x-authentication-token', this.getPasswordRecoveryAuthToken());
    return this.http.post<ValidateTokenResponse>(path, data, { headers }).toPromise();
  }

  async getRecoveryQuestion(isInternal = false): Promise<AccountRecoveryQuestionResponse> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/cadastro/senha/pergunta',
      oldBrowserFallbackPath: '/cobrancas/cadastro/senha/pergunta'
    })

    if (isInternal) {
      return this.http.get<AccountRecoveryQuestionResponse>(path).toPromise();
    }

    const headers = new HttpHeaders().set('x-authentication-token', this.getPasswordRecoveryAuthToken());
    return this.http.get<AccountRecoveryQuestionResponse>(path, { headers }).toPromise();
  }

  async sendRecoveryQuestionAnswer(answer: AccountRecoveryQuestionAnswer, isInternal = false): Promise<AccountRecoveryQuestionAnswerResponse> {
    const path = this.apiService.getEndpoint({
      apiKey: 'cdc-cobranca-orquestrador-registro',
      path: '/cobranca-orquestrador-registro/cobrancas/cadastro/senha/pergunta/responder',
      oldBrowserFallbackPath: '/cobrancas/cadastro/senha/pergunta/responder'
    })

    if (isInternal) {
      return this.http.post<AccountRecoveryQuestionAnswerResponse>(path, answer).toPromise();
    }

    const headers = new HttpHeaders().set('x-authentication-token', this.getPasswordRecoveryAuthToken());
    return this.http.post<AccountRecoveryQuestionAnswerResponse>(path, answer, { headers }).toPromise();
  }

  setPasswordRecoveryAuthToken(token: string) {
    sessionStorage.setItem(this.USER_PASSWORD_RECOVERY_TOKEN, token);
  }

  getPasswordRecoveryAuthToken() {
    return sessionStorage.getItem(this.USER_PASSWORD_RECOVERY_TOKEN);
  }

  setSelectedVerificationMethodInfo(verificationMethodInfo: SelectedVerificationMethodInfo) {
    sessionStorage.setItem(this.VERIFICATION_METHOD_INFO, JSON.stringify(verificationMethodInfo));
  }

  getSelectedVerificationMethodInfo(): SelectedVerificationMethodInfo | null {
    const data = sessionStorage.getItem(this.VERIFICATION_METHOD_INFO);

    if (data) {
      return JSON.parse(data);
    }

    return null;
  }

  setSecretKey(secret: SecretData) {
    sessionStorage.setItem(this.SECRET_KEY, JSON.stringify(secret));
  }

  getSecretKey(): SecretData | null {
    const data = sessionStorage.getItem(this.SECRET_KEY);

    if (data) {
      return JSON.parse(data);
    }

    return null;
  }

  clearSecretKey() {
    sessionStorage.removeItem(this.SECRET_KEY);
  }

  get apiUrlService(): string {
    if (BrowserUtils.isOldFirefox()) {
      return `${environment.cdccorpApiURLService}/v1`;
    }

    return `${environment.apiURLService}/v1`;
  }

  clearStorageData() {
    sessionStorage.removeItem(this.USER_PASSWORD_RECOVERY_TOKEN);
    sessionStorage.removeItem(this.VERIFICATION_METHOD_INFO);
    sessionStorage.removeItem(this.SECRET_KEY);
  }
}
